import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import Logo from '../components/Logo'

export default function Ligas() {
  const { user, usuario, minhasLigas, trocarLiga, selecionarLiga, carregarUsuario, signOut } = useAuth()
  const navigate = useNavigate()
  const [ligasDisponiveis, setLigasDisponiveis] = useState([])
  const [inscricoesPendentes, setInscricoesPendentes] = useState([])
  const [inscricoesRecusadas, setInscricoesRecusadas] = useState([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState(null)

  useEffect(() => { carregar() }, [])

  async function carregar() {
    setLoading(true)

    // Todas as ligas ativas com inscrições abertas
    const { data: todas } = await supabase
      .from('ligas').select('*').eq('ativa', true).order('criado_em', { ascending: false })

    // Inscrições do usuário ainda não aprovadas
    const { data: naoAprovadas } = await supabase
      .from('membros_liga').select('*, ligas(*)')
      .eq('usuario_id', user.id).eq('aprovado', false)

    // Separa pendentes (ativo != false) de recusadas (ativo = false)
    const pendentes = (naoAprovadas || []).filter(p => p.ativo !== false)
    const recusadas = (naoAprovadas || []).filter(p => p.ativo === false)

    // Busca membros aprovados/pendentes para excluir da lista de disponíveis
    // Recusados podem tentar de novo, então não bloqueiam a lista
    const { data: todosMembros } = await supabase
      .from('membros_liga').select('liga_id, ativo').eq('usuario_id', user.id)

    const idsAtivos = new Set(
      (todosMembros || []).filter(m => m.ativo !== false).map(m => m.liga_id)
    )
    const idsRecusados = new Set(recusadas.map(p => p.liga_id))

    // Ligas disponíveis = sem vínculo ativo e sem inscrição recusada pendente
    setLigasDisponiveis((todas || []).filter(l => !idsAtivos.has(l.id) && !idsRecusados.has(l.id)))
    setInscricoesPendentes(pendentes)
    setInscricoesRecusadas(recusadas)
    setLoading(false)
  }

  async function tentarNovamente(membroId, liga) {
    const { error } = await supabase.from('membros_liga')
      .update({ ativo: true })
      .eq('id', membroId)
    if (error) { setMsg({ texto: 'Erro ao tentar novamente.', tipo: 'erro' }); return }
    setMsg({ texto: `Solicitação para ${liga?.nome} reenviada! Aguarde aprovação do comissário.`, tipo: 'ok' })
    carregar()
  }

  async function solicitarInscricao(liga) {
    const { error } = await supabase.from('membros_liga').insert({
      liga_id: liga.id,
      usuario_id: user.id,
      vidas_iniciais: liga.vidas_padrao,
      vidas_restantes: liga.vidas_padrao,
      aprovado: false,
    })
    if (error) { setMsg({ texto: 'Erro ao solicitar inscrição.', tipo: 'erro' }); return }
    setMsg({ texto: `Inscrição na ${liga.nome} solicitada! Aguarde aprovação do comissário.`, tipo: 'ok' })
    carregar()
  }

  async function entrarNaLiga(ligaId) {
    const liga = minhasLigas.find(l => l.id === ligaId)
    if (!liga) return
    const { data: membro } = await import('../lib/supabase').then(({ supabase }) =>
      supabase.from('membros_liga')
        .select('id, liga_id, usuario_id, is_admin, vidas_iniciais, vidas_restantes, aprovado, ativo, pagamento_confirmado')
        .eq('liga_id', ligaId).eq('usuario_id', user.id).maybeSingle()
    )
    selecionarLiga(liga, membro)
    navigate('/')
  }

  if (loading) return <div style={s.loading}>Carregando...</div>

  return (
    <div style={s.wrap}>
      <div style={s.card}>
        <div style={s.logo}><Logo size={240} /></div>
        <h1 style={s.title}>Olá, {usuario?.primeiro_nome}!</h1>
        <p style={s.sub}>Selecione uma liga ou solicite sua inscrição</p>

        {/* Botão sair — sempre visível (especialmente útil para usuários pendentes) */}
        <button style={s.btnSair} onClick={async () => { await signOut(); navigate('/login') }}>
          🚪 Sair da conta
        </button>

        {msg && <div style={msg.tipo === 'ok' ? s.msgOk : s.msgErro}>{msg.texto}</div>}

        {/* Ligas ativas do usuário */}
        {minhasLigas.length > 0 && (
          <div style={s.secao}>
            <div style={s.secaoTitulo}>Suas ligas</div>
            {minhasLigas.map(liga => (
              <div key={liga.id} style={s.ligaCard}>
                <div style={s.ligaInfo}>
                  <div style={s.ligaNome}>{liga.nome}</div>
                  <div style={s.ligaSub}>{liga.ano} · {liga.membro?.vidas_restantes} vidas</div>
                </div>
                <button style={s.btnEntrar} onClick={() => entrarNaLiga(liga.id)}>
                  Entrar →
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Inscrições pendentes */}
        {inscricoesPendentes.length > 0 && (
          <div style={s.secao}>
            <div style={s.secaoTitulo}>Aguardando aprovação</div>
            {inscricoesPendentes.map(p => (
              <div key={p.id} style={s.ligaCardPendente}>
                <div style={s.ligaInfo}>
                  <div style={s.ligaNome}>{p.ligas?.nome}</div>
                  <div style={s.ligaSub}>{p.ligas?.ano}</div>
                </div>
                <span style={s.tagPendente}>⏳ Pendente</span>
              </div>
            ))}
          </div>
        )}

        {/* Inscrições recusadas */}
        {inscricoesRecusadas.length > 0 && (
          <div style={s.secao}>
            <div style={s.secaoTitulo}>Inscrição recusada</div>
            {inscricoesRecusadas.map(p => (
              <div key={p.id} style={s.ligaCardRecusada}>
                <div style={s.ligaInfo}>
                  <div style={s.ligaNome}>{p.ligas?.nome}</div>
                  <div style={s.ligaSub}>{p.ligas?.ano}</div>
                </div>
                <button style={s.btnTentarNovamente} onClick={() => tentarNovamente(p.id, p.ligas)}>
                  Tentar novamente
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Ligas disponíveis para inscrição */}
        {ligasDisponiveis.length > 0 && (
          <div style={s.secao}>
            <div style={s.secaoTitulo}>Ligas disponíveis</div>
            {ligasDisponiveis.map(liga => (
              <div key={liga.id} style={s.ligaCard}>
                <div style={s.ligaInfo}>
                  <div style={s.ligaNome}>{liga.nome}</div>
                  <div style={s.ligaSub}>
                    {liga.ano} · {liga.vidas_padrao} vidas
                    {liga.valor_buyin && ` · R$ ${Number(liga.valor_buyin).toFixed(0)} buy-in`}
                  </div>
                </div>
                <button style={s.btnInscrever} onClick={() => solicitarInscricao(liga)}>
                  Inscrever-se
                </button>
              </div>
            ))}
          </div>
        )}

        {minhasLigas.length === 0 && inscricoesPendentes.length === 0 && ligasDisponiveis.length === 0 && (
          <div style={s.vazio}>
            Nenhuma liga disponível no momento. Aguarde o comissário criar uma liga.
          </div>
        )}
      </div>
    </div>
  )
}

const s = {
  wrap: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', background: '#f5f5f5' },
  card: { background: 'white', borderRadius: 12, padding: '2rem', width: '100%', maxWidth: 420, boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  logo: { display: 'flex', justifyContent: 'center', marginBottom: '1rem' },
  title: { fontSize: 17, fontWeight: 500, textAlign: 'center', marginBottom: 4 },
  sub: { fontSize: 13, color: '#666', textAlign: 'center', marginBottom: '1.5rem' },
  loading: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#666' },
  secao: { marginBottom: '1.2rem' },
  secaoTitulo: { fontSize: 11, fontWeight: 500, color: '#888', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 },
  ligaCard: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#f9f9f9', borderRadius: 8, marginBottom: 6, border: '0.5px solid #eee' },
  btnSair: { display: 'block', margin: '0 auto 16px', background: 'none', border: '0.5px solid #ddd', borderRadius: 8, padding: '6px 16px', fontSize: 12, color: '#888', cursor: 'pointer' },
  ligaInfo: { flex: 1 },
  ligaNome: { fontSize: 14, fontWeight: 500 },
  ligaSub: { fontSize: 11, color: '#888', marginTop: 2 },
  btnEntrar: { padding: '7px 16px', background: '#D85A30', color: 'white', border: 'none', borderRadius: 6, fontSize: 13, cursor: 'pointer', fontWeight: 500, whiteSpace: 'nowrap' },
  btnInscrever: { padding: '7px 14px', background: '#0A2342', color: 'white', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
  tagPendente: { fontSize: 11, color: '#633806', whiteSpace: 'nowrap' },
  ligaCardRecusada: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: '#FFF5F5', borderRadius: 8, marginBottom: 6, border: '0.5px solid #E24B4A' },
  btnTentarNovamente: { padding: '6px 12px', background: 'none', border: '0.5px solid #E24B4A', color: '#A32D2D', borderRadius: 6, fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap' },
  msgOk: { background: '#EAF3DE', border: '0.5px solid #3B6D11', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#3B6D11' },
  msgErro: { background: '#FCEBEB', border: '0.5px solid #A32D2D', borderRadius: 8, padding: '8px 12px', marginBottom: 12, fontSize: 13, color: '#A32D2D' },
  vazio: { fontSize: 13, color: '#888', textAlign: 'center', padding: '1rem 0' },
}
